import { Dispatch, SetStateAction, memo, useMemo } from 'react';
import { Alert, Box, Center, Loader, Table as MantineTable, Text } from '@mantine/core';
import { type MRT_PaginationState } from 'mantine-react-table';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { formatMoneyString } from '../../../utils/strings';
import Table from '../../../components/Table';
import { useGetProductSalesDrillDown } from '../../../hooks/reports/queries';

interface DrillDownPanelProps {
  storeId: string;
  storeProductId: string;
  fromDate?: string;
  toDate?: string;
  timezone?: string;
}

const getEmployeeName = (employee: { name?: string } | null | undefined) => employee?.name || '—';

const formatDrillDownDate = (value?: string | Date | null) => (
  value ? moment(value).format('DD/MM/YY HH:mm') : '—'
);

function DrillDownPanel({ storeId, storeProductId, fromDate, toDate, timezone }: DrillDownPanelProps) {
  const { t } = useTranslation(['ReportsPage', 'FieldName']);
  const { data, isLoading, isError } = useGetProductSalesDrillDown(storeId, storeProductId, {
    fromDate,
    toDate,
    timezone,
  });

  if (isLoading) {
    return (
      <Center py="md">
        <Loader size="sm" />
      </Center>
    );
  }

  if (isError) {
    return (
      <Alert color="red" py="xs">
        {t('ReportsPage:productSalesReport.drillDown.error')}
      </Alert>
    );
  }

  const rows: any[] = data?.rows ?? [];

  if (rows.length === 0) {
    return (
      <Text size="sm" c="dimmed" py="xs" px="md">
        {t('ReportsPage:productSalesReport.drillDown.noData')}
      </Text>
    );
  }

  const translatePaymentStatus = (status?: string | null) => (
    status
      ? t(`ReportsPage:productSalesReport.paymentStatuses.${status}`, { defaultValue: status })
      : '—'
  );

  return (
    <Box px="md" py="xs" style={{ overflowX: 'auto' }}>
      <MantineTable striped withColumnBorders withTableBorder fz="sm">
        <MantineTable.Thead>
          <MantineTable.Tr>
            <MantineTable.Th>{t('FieldName:folio')}</MantineTable.Th>
            <MantineTable.Th>{t('FieldName:quantity')}</MantineTable.Th>
            <MantineTable.Th>{t('FieldName:total')}</MantineTable.Th>
            <MantineTable.Th>{t('FieldName:customer')}</MantineTable.Th>
            <MantineTable.Th>
              {t('ReportsPage:productSalesReport.drillDown.washedBy')}
            </MantineTable.Th>
            <MantineTable.Th>
              {t('ReportsPage:productSalesReport.drillDown.assignedEmployee')}
            </MantineTable.Th>
            <MantineTable.Th>{t('FieldName:createdDate')}</MantineTable.Th>
            <MantineTable.Th>{t('FieldName:cleanedDate')}</MantineTable.Th>
            <MantineTable.Th>{t('FieldName:collectedDate')}</MantineTable.Th>
            <MantineTable.Th>{t('FieldName:status')}</MantineTable.Th>
            <MantineTable.Th>
              {t('ReportsPage:productSalesReport.drillDown.paymentStatus')}
            </MantineTable.Th>
          </MantineTable.Tr>
        </MantineTable.Thead>
        <MantineTable.Tbody>
          {rows.map((row: any, index: number) => {
            const processedBy = row.processedBy ?? row.markedCleanedBy;
            const processedDate = row.processedDate ?? row.cleanedDateTime;
            const lineStatus = row.lineStatus ?? row.status;

            return (
              <MantineTable.Tr key={`${row.orderId}-${row.storeProductId}-${row.isBuyAndGetLine ? 'promo' : 'sale'}-${index}`}>
                <MantineTable.Td>{row.folio || '—'}</MantineTable.Td>
                <MantineTable.Td>{row.quantity}</MantineTable.Td>
                <MantineTable.Td>{formatMoneyString(row.lineTotal?.toFixed(2))}</MantineTable.Td>
                <MantineTable.Td>{row.customerName || '—'}</MantineTable.Td>
                <MantineTable.Td>{getEmployeeName(processedBy)}</MantineTable.Td>
                <MantineTable.Td>{getEmployeeName(row.assignedEmployee)}</MantineTable.Td>
                <MantineTable.Td>
                  {formatDrillDownDate(row.createdDate)}
                </MantineTable.Td>
                <MantineTable.Td>
                  {formatDrillDownDate(processedDate)}
                </MantineTable.Td>
                <MantineTable.Td>
                  {formatDrillDownDate(row.collectedDate)}
                </MantineTable.Td>
                <MantineTable.Td>
                  {lineStatus ? t(`FieldName:orderStatuses.${lineStatus}`, { defaultValue: lineStatus }) : '—'}
                </MantineTable.Td>
                <MantineTable.Td>{translatePaymentStatus(row.paymentStatus)}</MantineTable.Td>
              </MantineTable.Tr>
            );
          })}
        </MantineTable.Tbody>
        <MantineTable.Tfoot>
          <MantineTable.Tr>
            <MantineTable.Td colSpan={1}>
              <Text fw="bold" size="sm">{t('FieldName:total')}</Text>
            </MantineTable.Td>
            <MantineTable.Td>
              <Text fw="bold" size="sm">{data?.totalQuantity}</Text>
            </MantineTable.Td>
            <MantineTable.Td>
              <Text fw="bold" size="sm">{formatMoneyString(data?.totalAmount?.toFixed(2))}</Text>
            </MantineTable.Td>
            <MantineTable.Td colSpan={8} />
          </MantineTable.Tr>
        </MantineTable.Tfoot>
      </MantineTable>
    </Box>
  );
}

export default memo(function TableView({
  storeId,
  fromDate,
  toDate,
  timezone,
  tableInfoData = [],
  sectionsInfoData = [],
  groupBySection = false,
  totalRowsCount = 0,
  pages = 0,
  isError = false,
  isLoading = false,
  pagination,
  setPagination,
  footerInfo: _footerInfo
}: {
  storeId: string;
  fromDate?: string;
  toDate?: string;
  timezone?: string;
  tableInfoData: any[];
  sectionsInfoData: any[];
  groupBySection: boolean;
  footerInfo: {};
  totalRowsCount: any;
  pages: any;
  isError: boolean;
  isLoading: boolean;
  pagination: MRT_PaginationState;
  setPagination: Dispatch<SetStateAction<MRT_PaginationState>>;
}) {
  const { t } = useTranslation(['FieldName', 'ReportsPage']);

  const groupedTableData = useMemo(() => {
    return sectionsInfoData.map((section: any) => ({
      ...section,
      _id: `section-${section.sectionId || 'unsectioned'}`,
      rowType: 'section',
      name: section.sectionName,
      subRows: (section.products || []).map((product: any) => ({
        ...product,
        rowType: 'product',
      })),
    }));
  }, [sectionsInfoData]);

  const currentTableData = groupBySection ? groupedTableData : tableInfoData;

  const sumColumn = (fieldName: string) => {
    const sourceRows = groupBySection ? sectionsInfoData : tableInfoData;
    return sourceRows.reduce((prev, next) => prev + (next[fieldName] || 0), 0);
  };

  const defaultColumns = [
    {
      accessorFn: (row: any) => row.name,
      header: t('FieldName:name')
    },
    {
      accessorFn: (row: any) => formatMoneyString(row.productLineTotal.toFixed(2)),
      header: t('FieldName:total'),
      Footer: () => (
        <Text fw="bold">
          {formatMoneyString(
            tableInfoData.reduce((prev, next) => prev + next.productLineTotal, 0).toFixed(2)
          )}
        </Text>
      )
    },
    {
      accessorFn: (row: any) => row.productLineQty,
      header: t('FieldName:quantity')
    }
  ];

  const groupedColumns = [
    {
      accessorFn: (row: any) => row.sectionName,
      header: t('FieldName:section'),
      Cell: ({ row }: any) => (
        <Text fw={row.original.rowType === 'section' ? 'bold' : undefined} size="sm">
          {row.original.rowType === 'section' ? row.original.sectionName : ''}
        </Text>
      ),
    },
    {
      accessorFn: (row: any) => row.name,
      header: t('FieldName:product'),
      Cell: ({ row }: any) => (
        <Text fw={row.original.rowType === 'section' ? 'bold' : undefined} size="sm">
          {row.original.rowType === 'section' ? '' : row.original.name}
        </Text>
      ),
    },
    {
      accessorFn: (row: any) => row.productLineQty,
      header: t('FieldName:quantity'),
      Cell: ({ row }: any) => (
        <Text fw={row.original.rowType === 'section' ? 'bold' : undefined} size="sm">
          {row.original.productLineQty}
        </Text>
      ),
      Footer: () => (
        <Text fw="bold">{sumColumn('productLineQty')}</Text>
      )
    },
    {
      accessorFn: (row: any) => formatMoneyString((row.productLineGrossTotal || 0).toFixed(2)),
      header: t('ReportsPage:productSalesReport.totalSold'),
      Cell: ({ row }: any) => (
        <Text fw={row.original.rowType === 'section' ? 'bold' : undefined} size="sm">
          {formatMoneyString((row.original.productLineGrossTotal || 0).toFixed(2))}
        </Text>
      ),
      Footer: () => (
        <Text fw="bold">
          {formatMoneyString(sumColumn('productLineGrossTotal').toFixed(2))}
        </Text>
      )
    },
    {
      accessorFn: (row: any) => formatMoneyString((row.productLineDiscountTotal || 0).toFixed(2)),
      header: t('FieldName:discount'),
      Cell: ({ row }: any) => (
        <Text fw={row.original.rowType === 'section' ? 'bold' : undefined} size="sm">
          {formatMoneyString((row.original.productLineDiscountTotal || 0).toFixed(2))}
        </Text>
      ),
      Footer: () => (
        <Text fw="bold">
          {formatMoneyString(sumColumn('productLineDiscountTotal').toFixed(2))}
        </Text>
      )
    },
    {
      accessorFn: (row: any) => formatMoneyString((row.productLineTotal || 0).toFixed(2)),
      header: t('ReportsPage:productSalesReport.netTotal'),
      Cell: ({ row }: any) => (
        <Text fw={row.original.rowType === 'section' ? 'bold' : undefined} size="sm">
          {formatMoneyString((row.original.productLineTotal || 0).toFixed(2))}
        </Text>
      ),
      Footer: () => (
        <Text fw="bold">
          {formatMoneyString(sumColumn('productLineTotal').toFixed(2))}
        </Text>
      )
    }
  ];

  const columns = groupBySection ? groupedColumns : defaultColumns;
  const renderProductDetailPanel = ({ row }: { row: { original: any } }) => (
    <DrillDownPanel
      storeId={storeId}
      storeProductId={String(row.original._id)}
      fromDate={fromDate}
      toDate={toDate}
      timezone={timezone}
    />
  );

  const ungroupedTableConfigs = {
    manualPagination: false,
    renderDetailPanel: renderProductDetailPanel,
  };

  const groupedTableConfigs = {
    manualPagination: false,
    paginateExpandedRows: false,
    enableExpanding: true,
    enableExpandAll: false,
    getSubRows: (row: any) => row.subRows,
    getRowCanExpand: (row: { original: any }) => (
      row.original.rowType === 'section' || row.original.rowType === 'product'
    ),
    displayColumnDefOptions: {
      'mrt-row-expand': {
        header: '',
      },
    },
    localization: {
      expand: '',
      expandAll: '',
    },
    renderDetailPanel: ({ row }: { row: { original: any } }) => {
      if (row.original.rowType !== 'product') {
        return null;
      }

      return renderProductDetailPanel({ row });
    },
  };

  return (
    <Table
      tableColumns={columns}
      pagination={pagination}
      setPagination={setPagination}
      tableData={currentTableData}
      isError={isError}
      paginationIsLoading={isLoading}
      numPages={pages}
      totalRowsCount={totalRowsCount}
      overrideTableConfigs={groupBySection ? groupedTableConfigs : ungroupedTableConfigs}
    />
  );
});
